import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "@dr.pogodin/react-helmet";
import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";
import { SkipToContent } from "@/components/lembra/SkipToContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { emailSchema, fullNameSchema, trimmedStringSchema } from "@/lib/schemas";

// Length caps match the edge function so the client never sends a payload
// the server will reject anyway. Keep these in sync with the edge-side
// validation in send-email.
const MAX_SUBJECT = 200;
const MAX_MESSAGE = 4000;

const contactSchema = z.object({
  name: fullNameSchema,
  email: emailSchema,
  subject: trimmedStringSchema({ min: 1, max: MAX_SUBJECT, label: "a subject" }),
  message: trimmedStringSchema({ min: 1, max: MAX_MESSAGE, label: "your message" }),
});

type ContactForm = z.infer<typeof contactSchema>;

const Contact = () => {
  const [sent, setSent] = useState(false);

  const form = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", subject: "", message: "" },
  });

  const submitting = form.formState.isSubmitting;
  const message = form.watch("message") ?? "";
  const submittedName = form.getValues("name");

  const onSubmit = async (values: ContactForm) => {
    try {
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          type: "contact_form",
          to: "support@lembra.com.au",
          vars: {
            fromName: values.name,
            fromEmail: values.email,
            subject: values.subject,
            message: values.message,
          },
        },
      });
      if (error) throw error;
      setSent(true);
      toast.success("Message sent. We'll get back to you within a business day.");
    } catch (err) {
      const description = err instanceof Error ? err.message : "Something went wrong.";
      toast.error(`Couldn't send that message. ${description}`);
    }
  };

  const resetForSecondMessage = () => {
    setSent(false);
    form.reset();
  };

  return (
    <>
      <PageMeta
        title="Contact Lembra | Workplace celebration questions"
        description="Get in touch with the Lembra team about workplace celebrations, vendor partner applications, or anything else. Based in Melbourne, replies within one business day."
        path="/contact"
      />
      <Helmet>
        {/* MS188: BreadcrumbList + ContactPage. Helps Google show
            this surface as the canonical "contact Lembra" landing
            spot for the search query rather than guessing from the
            homepage. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          url: "https://lembra.com.au/contact",
          name: "Contact Lembra",
          description:
            "Get in touch with the Lembra team about workplace celebrations, partner vendor applications, or anything else. Based in Melbourne, replies within one business day.",
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Lembra", item: "https://lembra.com.au" },
            { "@type": "ListItem", position: 2, name: "Contact", item: "https://lembra.com.au/contact" },
          ],
        })}</script>
      </Helmet>
      <SkipToContent />
    <div className="min-h-screen bg-cream flex flex-col">
      <header className="py-6 px-6 md:px-10">
        <Logo size="md" />
      </header>

      {/* MS6, Heidi Contact pattern. Hero band (cream→butter)
          with eyebrow pill + serif H1 italic-emphasis + DM Sans
          lede; below it two action cards (Email + Office) and the
          message form. Copy retoned for multi-vendor positioning
          (was "bakery keen to partner" → now "vendor wanting to
          join the catalogue"). */}
      <main id="main" className="flex-1 w-full">
        {/* Hero band */}
        <section
          className="px-6 md:px-10"
          style={{
            background:
              "linear-gradient(180deg, #FBF3D0 0%, #F7E8A4 55%, #FBF3D0 100%)",
            padding: "clamp(48px, 6vw, 80px) 0 clamp(40px, 5vw, 64px)",
          }}
        >
          <div
            className="text-center"
            style={{ maxWidth: 760, margin: "0 auto" }}
          >
            <span
              className="inline-flex items-center rounded-full px-3 py-1"
              style={{
                background: "rgba(29,52,97,0.06)",
                border: "1px solid rgba(29,52,97,0.08)",
                fontFamily:
                  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(29,52,97,0.7)",
              }}
            >
              Contact
            </span>
            <h1
              style={{
                fontFamily: "Newsreader, Georgia, serif",
                fontWeight: 500,
                fontSize: "clamp(2.2rem, 4.4vw, 3.4rem)",
                lineHeight: 1.06,
                letterSpacing: "-0.02em",
                color: "#1D3461",
                margin: "20px 0 16px",
                maxWidth: "20ch",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              Hello{" "}
              <em
                style={{
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#C4564A",
                }}
              >
                there
              </em>
              .
            </h1>
            <p
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: "clamp(0.95rem, 1.4vw, 1.1rem)",
                lineHeight: 1.55,
                color: "rgba(45,41,38,0.72)",
                margin: "0 auto",
                maxWidth: "48ch",
              }}
            >
              Questions about the platform, a celebration you'd like sorted,
              or a vendor wanting to join the catalogue. We'd love to hear
              from you.
            </p>
          </div>
        </section>

        <div
          className="mx-auto px-6 md:px-10 pb-20"
          style={{ maxWidth: 760, width: "100%" }}
        >
          <section
            className="grid gap-5 md:grid-cols-2"
            style={{ marginTop: "clamp(40px, 5vw, 56px)", marginBottom: 40 }}
          >
            <div
              className="rounded-2xl bg-white p-6"
              style={{
                border: "1px solid rgba(29,52,97,0.06)",
                boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <Mail className="h-5 w-5 text-coral" strokeWidth={1.8} />
                <h2
                  style={{
                    fontFamily: "Newsreader, Georgia, serif",
                    fontWeight: 500,
                    fontSize: "1.15rem",
                    color: "#1D3461",
                    margin: 0,
                  }}
                >
                  Email
                </h2>
              </div>
              <p
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: "0.9rem",
                  lineHeight: 1.55,
                  color: "rgba(45,41,38,0.7)",
                  margin: "0 0 10px",
                }}
              >
                Fastest way to reach us. Replies within one business day.
              </p>
              <a
                href="mailto:support@lembra.com.au"
                className="font-semibold text-navy hover:underline"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                support@lembra.com.au
              </a>
            </div>

            <div
              className="rounded-2xl bg-white p-6"
              style={{
                border: "1px solid rgba(29,52,97,0.06)",
                boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="h-5 w-5 text-coral" strokeWidth={1.8} />
                <h2
                  style={{
                    fontFamily: "Newsreader, Georgia, serif",
                    fontWeight: 500,
                    fontSize: "1.15rem",
                    color: "#1D3461",
                    margin: 0,
                  }}
                >
                  Office
                </h2>
              </div>
              <p
                style={{
                  fontFamily: "DM Sans, sans-serif",
                  fontSize: "0.9rem",
                  lineHeight: 1.55,
                  color: "rgba(45,41,38,0.7)",
                  margin: "0 0 10px",
                }}
              >
                By appointment. We do most of our chatting over email.
              </p>
              <p
                className="font-semibold text-navy"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                11 Wilson Street<br />
                South Yarra VIC 3141
              </p>
            </div>
          </section>

          <section
            className="rounded-2xl bg-white p-6 md:p-8"
            style={{
              border: "1px solid rgba(29,52,97,0.06)",
              boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
            }}
          >
            <h2
              style={{
                fontFamily: "Newsreader, Georgia, serif",
                fontWeight: 500,
                fontSize: "clamp(1.4rem, 2.4vw, 1.85rem)",
                lineHeight: 1.18,
                color: "#1D3461",
                margin: "0 0 6px",
                letterSpacing: "-0.01em",
              }}
            >
              Send us a{" "}
              <em
                style={{
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#C4564A",
                }}
              >
                message
              </em>
              .
            </h2>
            <p
              className="mb-6"
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: "0.875rem",
                color: "rgba(45,41,38,0.65)",
                margin: "0 0 24px",
              }}
            >
              This goes straight to <strong>support@lembra.com.au</strong>.
            </p>

          {sent ? (
            <div className="flex flex-col items-center text-center py-6 gap-3">
              <CheckCircle2 className="h-12 w-12 text-coral" />
              <h3 className="text-xl font-bold text-navy">
                Thanks, {submittedName.trim().split(" ")[0] || "friend"}.
              </h3>
              <p className="text-charcoal/70 max-w-sm">
                Your message is on its way. We'll be back to you within one business day.
              </p>
              <button
                type="button"
                onClick={resetForSecondMessage}
                className="text-sm text-coral hover:underline mt-2"
              >
                Send another message
              </button>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-navy">Name</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder="Jane Smith"
                            maxLength={100}
                            autoComplete="name"
                            className="rounded-xl"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-navy">Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="you@company.com"
                            autoComplete="email"
                            /* MS159: RFC 5321 max email length. The
                               other Contact fields already had a
                               maxLength (name=100, subject + message
                               via MAX_*); email was the one gap. */
                            maxLength={320}
                            className="rounded-xl"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-navy">Subject</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="What's this about?"
                          maxLength={MAX_SUBJECT}
                          className="rounded-xl"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-navy">Message</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Tell us what's on your mind."
                          maxLength={MAX_MESSAGE}
                          rows={6}
                          className="rounded-xl resize-none"
                          {...field}
                        />
                      </FormControl>
                      <div className="flex items-center justify-between">
                        <FormMessage />
                        <p className="text-xs text-charcoal/50 text-right ml-auto">
                          {message.length}/{MAX_MESSAGE}
                        </p>
                      </div>
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full rounded-full"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    "Send message"
                  )}
                </Button>

                <p className="text-xs text-charcoal/50 text-center">
                  By sending, you agree to our{" "}
                  <Link to="/privacy" className="underline hover:text-navy">Privacy Policy</Link>.
                </p>
              </form>
            </Form>
          )}
        </section>

          <div className="mt-10">
            <Link
              to="/"
              className="text-sm text-coral hover:underline"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </main>
    </div>
    </>
  );
};

export default Contact;
